"use client";

import { useState, useCallback, useEffect } from "react";
import { useTranslations } from "use-intl";

import { ReleaseImageLightbox } from "@/components/release-image-lightbox";
import { useHashModal } from "@/hooks/use-hash-modal";
import type { ReleaseImage } from "@garimpa/api/music/getReleaseImages";
import {
  buildReleaseGalleryImageSrcSet,
  buildReleaseGalleryImageUrl,
} from "@garimpa/lib/imageUrls";

interface ReleaseImageGalleryProps {
  images: ReleaseImage[];
  releaseId: string;
  title: string;
}

const RELEASE_IMAGES_MODAL = "release-images";

interface ReleaseImageZoomPayload {
  index: number;
}

export function ReleaseImageGallery({ images, releaseId, title }: ReleaseImageGalleryProps) {
  const t = useTranslations("release");
  const hashModal = useHashModal();
  const modalOwnerId = `release:${releaseId}`;
  const [selectedIndex, setSelectedIndex] = useState(0);

  const zoomState = hashModal.getState<ReleaseImageZoomPayload>(RELEASE_IMAGES_MODAL, {
    ownerId: modalOwnerId,
  });
  const zoomPayload = zoomState.open ? zoomState.payload : null;
  const zoomOpen = zoomState.open;

  const closeZoom = useCallback(() => {
    hashModal.close(RELEASE_IMAGES_MODAL, { ownerId: modalOwnerId });
  }, [hashModal, modalOwnerId]);

  const goNext = useCallback(() => {
    setSelectedIndex((i) => (i + 1) % images.length);
  }, [images.length]);

  const goPrev = useCallback(() => {
    setSelectedIndex((i) => (i - 1 + images.length) % images.length);
  }, [images.length]);

  // Keyboard navigation for thumbnails
  useEffect(() => {
    if (images.length <= 1 || zoomOpen) return;

    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === "ArrowRight") goNext();
      else if (e.key === "ArrowLeft") goPrev();
    }

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [images.length, zoomOpen, goNext, goPrev]);

  // Don't render anything while loading or when 0-1 images (parent cover handles the single image)
  if (images.length <= 1) {
    return null;
  }

  return (
    <>
      {/* Horizontal thumbnail strip */}
      <div className="flex gap-2 overflow-x-auto pb-1 mt-3 min-w-0 w-full">
        {images.map((image, idx) => {
          const thumbnailUrl = buildReleaseGalleryImageUrl(releaseId, idx, { width: 150 });
          const thumbnailSrcSet = buildReleaseGalleryImageSrcSet(releaseId, idx, [150, 320]);

          return (
            <button
              key={`${image.uri150 ?? image.uri}-${idx}`}
              onClick={() => {
                setSelectedIndex(idx);
                hashModal.open<ReleaseImageZoomPayload>(
                  RELEASE_IMAGES_MODAL,
                  {
                    index: idx,
                  },
                  { ownerId: modalOwnerId },
                );
              }}
              className={`relative shrink-0 w-16 h-16 rounded-md overflow-hidden border-2 transition-colors ${
                idx === selectedIndex ? "border-primary" : "border-transparent hover:border-border"
              }`}
              aria-label={t("photoOf", { number: idx + 1, total: images.length })}
            >
              <img
                src={thumbnailUrl}
                srcSet={thumbnailSrcSet}
                sizes="64px"
                alt={`${title} - ${t("photoOf", { number: idx + 1, total: images.length })}`}
                className="w-full h-full object-cover"
                loading="lazy"
                decoding="async"
              />
              {image.assetType === "pdf" && (
                <span className="absolute bottom-1 right-1 rounded bg-black/70 px-1 py-0.5 text-[10px] font-bold leading-none text-white">
                  PDF
                </span>
              )}
            </button>
          );
        })}
      </div>
      {zoomOpen && zoomPayload && (
        <ReleaseImageLightbox
          analyticsSurface="release_detail"
          images={images}
          imageCount={images.length}
          initialIndex={zoomPayload.index}
          onClose={closeZoom}
          onIndexChange={setSelectedIndex}
          releaseId={releaseId}
          title={title}
        />
      )}
    </>
  );
}
